import React from "react";
import EngineeringIcon from "../EngineeringIcons";
import AnimatedDivider from "../AnimatedDivider";

const FarmMachinery: React.FC = () => {
  const machineryTypes = [
    {
      title: "Harvesting Equipment",
      description: "Advanced harvesting machinery designed for efficiency and crop preservation.",
      icon: "🌾",
      features: ["Automated harvesters", "Crop sorting systems", "Gentle handling technology"]
    },
    {
      title: "Food Processing Lines",
      description: "Complete food processing systems from raw materials to finished products.",
      icon: "🥫",
      features: ["Automated processing", "Quality control", "Packaging systems"]
    },
    {
      title: "Irrigation Systems",
      description: "Smart irrigation solutions for optimal water management and crop growth.",
      icon: "💧",
      features: ["Drip irrigation", "Smart sensors", "Water conservation"]
    },
    {
      title: "Storage Solutions",
      description: "Modern storage facilities and equipment for agricultural products.",
      icon: "🏭",
      features: ["Climate control", "Pest management", "Inventory tracking"]
    }
  ];

  const services = [
    "Custom machinery design and fabrication",
    "Complete food processing line installation",
    "Agricultural automation systems",
    "Maintenance and repair services",
    "Training and technical support",
    "Spare parts supply and management"
  ];

  const benefits = [
    {
      title: "Increased Productivity",
      description: "Automated systems that boost agricultural output by up to 300%",
      icon: "📈"
    },
    {
      title: "Reduced Labor Costs",
      description: "Smart machinery that minimizes manual labor requirements",
      icon: "💰"
    },
    {
      title: "Improved Quality",
      description: "Consistent processing that ensures high-quality agricultural products",
      icon: "⭐"
    },
    {
      title: "Sustainable Practices",
      description: "Eco-friendly solutions that promote environmental conservation",
      icon: "🌍"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-600 to-green-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-pulley-bounce">
                <EngineeringIcon type="pulley" size="lg" className="text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-bold mb-6">Farm Machinery & Food Processing</h1>
            <p className="text-xl mb-8">
              Designing and fabricating advanced agricultural machinery and complete food processing lines. 
              From automated farming equipment to industrial food production systems.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/20 px-4 py-2 rounded-full">Agricultural Innovation</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Food Security</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Automation Excellence</span>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Machinery Types */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Machinery Solutions</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive agricultural machinery and food processing equipment designed for efficiency, 
              reliability, and sustainability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {machineryTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 engineering-card">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4 animate-bounce-slow">{type.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-800">{type.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{type.description}</p>
                <ul className="space-y-2">
                  {type.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <EngineeringIcon type="bolt" size="sm" className="text-green-500 mr-3" animated={false} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="tools" />

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Choose Our Solutions?</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our farm machinery and food processing solutions deliver measurable benefits for your agricultural operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors">
                <div className="text-4xl mb-4 animate-pulley-bounce">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="mixed" />

      {/* Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive support for all your agricultural machinery and food processing needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center">
                  <EngineeringIcon type="wrench" size="md" className="text-green-500 mr-4" animated={false} />
                  <span className="font-semibold text-gray-800">{service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-green-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Transform Your Agricultural Operations</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join the agricultural revolution with our advanced machinery and food processing solutions. 
            Let's build a more efficient and sustainable future for farming in Ekumfiman.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="pulley" size="sm" animated={false} />
              Get a Quote
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="gear" size="sm" />
              View Our Equipment
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FarmMachinery; 